'use client'

import { Tag } from '@/components/ui/tag'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { TCardFacet, TFacet } from '@/types/collection'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import { sortedAttributeCardsUnderwear } from '../../../constants'
import { useCollectionFilterStoreV2 } from '../../../store/collection-filter-store-v2'
import { shouldAddNofollowToFilterLinks } from '../../../utils/filter-utils'
import { updateUrl } from '../../../utils/url-utils'
import { buildFilterUrl } from '../element/filter-url-builder'

type Props = {
  imageUrl: string
  imageHoverUrl?: string
  title: string
  description?: string
  productCount?: number
  filterValue: string
  filterKey: string
  activeKey?: string
  isUnderWare?: boolean
  onChangeFilter?: (value: string) => void
  hasConcatActive?: boolean
  hasConcatParent?: boolean
  isOpenNewTab?: boolean
  originalSlug?: string
  facets?: TFacet[]
  cardFacets?: TCardFacet[]
  isAllowMultiple?: boolean
  isConcatSlug?: boolean
  isVertical?: boolean
  isActive?: boolean
  shouldBeDimmed?: boolean
}

export const FilterCard = ({
  imageUrl,
  imageHoverUrl,
  title,
  description,
  productCount,
  filterValue,
  filterKey,
  isUnderWare,
  onChangeFilter,
  hasConcatActive,
  hasConcatParent,
  isOpenNewTab,
  originalSlug,
  facets,
  cardFacets,
  isAllowMultiple = false,
  isConcatSlug = false,
  isVertical,
  isActive: isActiveProp,
  shouldBeDimmed: shouldBeDimmedProp,
}: Props) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const setFilter = useCollectionFilterStoreV2((s) => s.setFilter)
  const currentFilters = useCollectionFilterStoreV2((s) => s.filters)

  const isActive =
    isActiveProp !== undefined
      ? isActiveProp
      : searchParams.get(filterKey) === filterValue ||
        (isUnderWare && pathname.includes(filterValue))

  // Build the URL for this filter using buildFilterUrl (same as filter-checkbox)
  const getFilterUrl = () => {
    // For open new tab, use simple collection URL
    if (isOpenNewTab) {
      return `/collection/${filterValue}`
    }

    // Use buildFilterUrl for proper URL construction with concat slug support
    if (originalSlug) {
      return buildFilterUrl({
        originalSlug,
        currentFilters,
        facetKey: filterKey,
        facetValue: filterValue,
        isChecked: !isActive, // Toggle state
        isMultiple: isAllowMultiple,
        isConcatSlug,
        facets,
        cardFacets,
        currentPathname: pathname,
      })
    }

    // Fallback for underwear or basic filtering
    if (isUnderWare) {
      return isActive
        ? '/collection/quan-lot-nam'
        : `/collection/quan-lot-nam-${filterValue}`
    }

    const params = new URLSearchParams(searchParams.toString())
    if (isActive) {
      params.delete(filterKey)
    } else {
      params.set(filterKey, filterValue)
    }
    const queryString = params.toString()
    return queryString ? `${pathname}?${queryString}` : pathname
  }

  const handleClick = (e: React.MouseEvent) => {
    // Handle new tab opening
    if (isOpenNewTab) {
      return // Let the link handle it
    }

    // Prevent default link navigation, we'll handle it with updateUrl
    e.preventDefault()

    // Original behavior
    const params = new URLSearchParams(searchParams.toString())

    if (isActive) {
      params.delete(filterKey)
      setFilter(filterKey, undefined)
    } else {
      params.set(filterKey, filterValue)
      setFilter(filterKey, filterValue)
    }

    const urlUnderWare = `/collection/quan-lot-nam-${filterValue}`

    if (isUnderWare) {
      updateUrl(isActive ? '/collection/quan-lot-nam' : urlUnderWare)
    } else {
      onChangeFilter?.(isActive || hasConcatActive ? '' : filterValue)
    }

    document.getElementById('collection-listing')?.scrollIntoView({
      behavior: 'smooth',
    })
  }

  const isActiveUnderWare = sortedAttributeCardsUnderwear.find((item) =>
    pathname.includes(item.seo_alias)
  )

  const shouldBeDimmed =
    shouldBeDimmedProp !== undefined
      ? shouldBeDimmedProp
      : (!isActive &&
          (searchParams.get(filterKey) ||
            (isUnderWare &&
              !pathname.includes(filterValue) &&
              isActiveUnderWare))) ||
        (hasConcatParent && !hasConcatActive && !isUnderWare)

  // Check if should add nofollow
  const shouldAddNofollow = shouldAddNofollowToFilterLinks(
    currentFilters,
    facets,
    cardFacets
  )

  // Build rel attribute: merge nofollow with existing noopener noreferrer logic
  const getRelAttribute = () => {
    if (isOpenNewTab) {
      return shouldAddNofollow
        ? 'noopener noreferrer nofollow'
        : 'noopener noreferrer'
    }
    return shouldAddNofollow ? 'nofollow' : undefined
  }

  const filterUrl = getFilterUrl()

  return (
    <Link
      href={filterUrl}
      target={isOpenNewTab ? '_blank' : undefined}
      rel={getRelAttribute()}
      onClick={handleClick}
      className='block'
      ga-tracking-value={`filter-card__${filterKey}`}
      ga-tracking-label={filterKey}
    >
      <figure className='cursor-pointer'>
        <picture
          className={cn(
            'group block overflow-hidden rounded-xl',
            shouldBeDimmed && 'opacity-40'
          )}
        >
          <div
            className={cn(
              'relative',
              isVertical ? 'aspect-[255/334] min-h-[180px]' : 'aspect-[328/159]'
            )}
          >
            {/* Base image */}
            <Image
              src={ImageHelper.getImageUrl(imageUrl, 'l')}
              alt={title}
              fill
              sizes='225px'
              className='object-cover transition-opacity duration-300'
            />
            {/* Hover image: stacked, preloaded, hidden via opacity (not display) */}
            {imageHoverUrl && (
              <Image
                src={ImageHelper.getImageUrl(imageHoverUrl, 'l')}
                alt=''
                aria-hidden
                fill
                sizes='225px'
                loading='eager'
                className='pointer-events-none object-cover opacity-0 transition-opacity duration-300 group-hover:opacity-100'
              />
            )}
          </div>
        </picture>
        <figcaption className='mt-2 text-left'>
          {title ? (
            <p
              className={cn(
                'line-clamp-1 font-sans text-sm font-medium leading-4.5 text-neutral-900 lg:text-lg lg:leading-5.5',
                isActive && 'text-primary'
              )}
            >
              {title}
            </p>
          ) : null}
          {description ? (
            <p className='line-clamp-2 font-sans text-xs leading-3.5 text-neutral-400 lg:text-sm lg:leading-4.5'>
              {description}
            </p>
          ) : null}
          {productCount && productCount > 0 && (
            <Tag
              title={`${productCount} sản phẩm`}
              className='mt-2 h-[23px] rounded border-neutral-300 px-2 py-1 text-xs font-normal leading-3 text-neutral-300 hover:border-neutral-400 lg:h-6.5 lg:text-sm lg:leading-4.5'
            />
          )}
        </figcaption>
      </figure>
    </Link>
  )
}
